import type { DepartureTimeSlot, SuggestedFilters } from '@/src/types/aiRecommendation'
import type { FilterData } from '@/src/types/flight'

const DEPARTURE_TIME_SLOTS = new Set<DepartureTimeSlot>(['0600', '1200', '1800', '2000'])

const isDepartureTimeSlot = (value: string): value is DepartureTimeSlot =>
    DEPARTURE_TIME_SLOTS.has(value as DepartureTimeSlot)

const normalizeCode = (value: string) => value?.trim().toUpperCase()

const isRangeApplied = (currentMin: number, currentMax: number, defaultMin: number, defaultMax: number) =>
    currentMin !== defaultMin || currentMax !== defaultMax

export interface ActiveFiltersToSuggestedFiltersParams {
    filterData: FilterData | undefined
    leg: 0 | 1
    searchFrom?: string
    searchTo?: string
    recommendedFilter?: {
        checkedBaggageIncluded?: boolean
        hideBudgetAirlines?: boolean
    }
    /** Same factor used when applying AI price filters (rate + buffer). Defaults to 1. */
    priceConversionFactor?: number
}

/**
 * Builds `SuggestedFilters` from the current SRP filter UI state for the given leg.
 * Inverse of `useApplySuggestedFilters` — used as `currentlyAppliedFilters` in AI manager requests.
 */
export function activeFiltersToSuggestedFilters ({
    filterData,
    leg,
    searchFrom,
    searchTo,
    recommendedFilter,
    priceConversionFactor = 1
}: ActiveFiltersToSuggestedFiltersParams): SuggestedFilters | null {
    if (!filterData) return null

    const result: SuggestedFilters = {}
    const factor = priceConversionFactor > 0 ? priceConversionFactor : 1

    const selectedStops = filterData.NewStops?.[leg]?.selectedValues
    if (selectedStops?.length) {
        if (selectedStops.length === 1 && selectedStops[0] === 0) {
            result.nonStop = true
        } else {
            result.stops = [...selectedStops]
        }
    }

    const departureSlots = filterData.departureTimeFrom?.[leg]?.selectedValues?.filter(isDepartureTimeSlot)
    if (departureSlots?.length) {
        result.departureTimeSlots = [...departureSlots]
    }

    const arrivalSlots = filterData.arrivalTimeFrom?.[leg]?.selectedValues?.filter(isDepartureTimeSlot)
    if (arrivalSlots?.length) {
        result.arrivalTimeSlots = [...arrivalSlots]
    }

    const airlineCodes = filterData.airlineFilter?.[leg]?.selectedValues
    if (airlineCodes?.length) {
        result.airlineCodes = airlineCodes.map(code => normalizeCode(code))
    }

    const journeyPrice = filterData.journeyPrice?.[leg]
    if (journeyPrice?.current) {
        const { min: defaultMin, max: defaultMax, current } = journeyPrice
        if (isRangeApplied(current.min, current.max, defaultMin, defaultMax)) {
            result.priceRange = {
                min: Math.ceil(current.min * factor),
                max: Math.floor(current.max * factor)
            }
        }
    }

    const tripDuration = filterData.tripDuration?.[leg]
    if (tripDuration?.current) {
        const { min: defaultMin, max: defaultMax, current } = tripDuration
        if (isRangeApplied(current.min, current.max, defaultMin, defaultMax)) {
            result.tripDurationRange = {}
            if (current.min !== defaultMin) result.tripDurationRange.min = current.min
            if (current.max !== defaultMax) result.tripDurationRange.max = current.max
        }
    }

    const layoverDuration = filterData.layoverDuration?.[leg]
    if (layoverDuration?.current) {
        const { min: defaultMin, max: defaultMax, current } = layoverDuration
        if (isRangeApplied(current.min, current.max, defaultMin, defaultMax)) {
            result.layoverDurationRange = {}
            if (current.min !== defaultMin) result.layoverDurationRange.min = current.min
            if (current.max !== defaultMax) result.layoverDurationRange.max = current.max
        }
    }

    const layoverAirportCodes = filterData.layover_Airport?.[leg]?.selectedOptions
    if (layoverAirportCodes?.length) {
        result.layoverAirportCodes = layoverAirportCodes.map(code => normalizeCode(code))
    }

    const nbGroups = filterData.nbAirportFilter?.[leg] || []
    const departureCity = (leg === 0 ? searchFrom : searchTo)?.toUpperCase()
    const arrivalCity = (leg === 0 ? searchTo : searchFrom)?.toUpperCase()
    const departureAirportCodes: string[] = []
    const arrivalAirportCodes: string[] = []

    nbGroups.forEach(group => {
        const groupCode = group.airportCode?.toUpperCase()
        const selected = (group.selectedValues || []).map(code => normalizeCode(code))
        if (!selected.length || !groupCode) return

        if (groupCode === departureCity) {
            departureAirportCodes.push(...selected)
        } else if (groupCode === arrivalCity) {
            arrivalAirportCodes.push(...selected)
        }
    })

    if (departureAirportCodes.length || arrivalAirportCodes.length) {
        result.includedAirportCodes = {}
        if (departureAirportCodes.length) {
            result.includedAirportCodes.departureAirportCodes = Array.from(new Set(departureAirportCodes))
        }
        if (arrivalAirportCodes.length) {
            result.includedAirportCodes.arrivalAirportCodes = Array.from(new Set(arrivalAirportCodes))
        }
    }

    if (recommendedFilter?.checkedBaggageIncluded) {
        result.checkedBaggageIncluded = true
    }
    if (recommendedFilter?.hideBudgetAirlines) {
        result.hideBudgetAirlines = true
    }

    return Object.keys(result).length ? result : null
}
